import React, { ComponentPropsWithoutRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  WebBodyText14,
  WebBodyText16,
  WebBodyText20,
  WebCaption10,
} from '@fintronners/react-ui/src/GlobalStyling/webTypography';
import cn from '@/utils/tailwind';
import {
  formatPercent,
  thousandFormatNumbroCurrency,
} from '@fintronners/react-utils/src/numberUtilsTSX';
import { convertISODateStringToDisplayString } from '@fintronners/react-utils/src/dateUtils';

type FixedIncomeTradeSummaryProps = Omit<ComponentPropsWithoutRef<'div'>, 'children'> & {
  title?: string;
  data: {
    orderType: 'buy' | 'sell';
    quantity: number;
    yieldMaturity: number;
    yieldToWorst?: number;
    limitPrice: number;
    fillingAmounts?:
      | {
          accrued: number;
          remaining: number;
        }
      | undefined;
    issuerName: string;
    couponRate: number;
    maturityDate?: string;
    isin: string;
    faceValuePerBond: number;
    accruedInterestPerBond: number;
    markup: number;
    commission: number;
    orderValue: number;
    netAmount: number;
  };
  isEstimated?: boolean;
  asOf?: Date;
  layoutType?: 'grid' | 'list';
};

const FixedIncomeTradeSummary: React.FC<FixedIncomeTradeSummaryProps> = ({
  title,
  data,
  className,
  layoutType = 'list',
  asOf,
  isEstimated = false,
  ...otherProps
}) => {
  const { t } = useTranslation();

  const isGrid = layoutType === 'grid';
  const isList = layoutType === 'list';

  const TitleText = isGrid ? WebBodyText16.Bold : WebBodyText14.Bold;
  const LabelText = isGrid ? WebBodyText14.Bold : WebBodyText14.Regular;
  const ValueText = isGrid ? WebBodyText16.Regular : WebBodyText14.Bold;
  const AsOfText = WebCaption10.Regular;

  const isPartialFill = Boolean(data?.fillingAmounts);

  const displayableData = {
    orderType:
      data.orderType === 'buy'
        ? t('miscScreens.tradeOptions.tradeLimitBuyOrderType')
        : t('miscScreens.tradeOptions.tradeLimitSellOrderType'),
    quantity: t('miscScreens.tradeOptions.fixedIncome.tradeSummary.quantityValue', {
      count: data.quantity,
    }),
    yieldMaturity: formatPercent(data.yieldMaturity / 100),
    yieldToWorst: data.yieldToWorst ? formatPercent(data.yieldToWorst / 100) : null,
    fillingAmounts: t('miscScreens.tradeOptions.fixedIncome.tradeSummary.partialFillValue', {
      accrued: String(data?.fillingAmounts?.accrued),
      remaining: String(data?.fillingAmounts?.remaining),
    }),
    limitPrice: thousandFormatNumbroCurrency(data.limitPrice),
    timeInForce: t('miscScreens.tradeOptions.fixedIncome.tradeSummary.timeInForceValue', {
      count: 1,
    }),
    issuerName: data.issuerName ?? '-',
    couponRate: formatPercent(data.couponRate / 100),
    maturityDate: convertISODateStringToDisplayString(data?.maturityDate ?? '-', '/'),
    isin: data.isin ?? '-',
    faceValuePerBond: thousandFormatNumbroCurrency(data.faceValuePerBond),
    accruedInterestPerBond: data.accruedInterestPerBond
      ? thousandFormatNumbroCurrency(data.accruedInterestPerBond)
      : '-',
    markup: thousandFormatNumbroCurrency(data.markup),
    commission: thousandFormatNumbroCurrency(data.commission),
    orderValue: thousandFormatNumbroCurrency(data.orderValue),
    netPrice: thousandFormatNumbroCurrency(data.netAmount),
  };

  return (
    <div {...otherProps} className={cn('flex flex-col gap-5', className)}>
      {title && (
        <div className="mt-7">
          <WebBodyText20.Bold className="text-darkestBlue">{title}</WebBodyText20.Bold>
        </div>
      )}

      <div
        className={cn(
          isGrid && 'flex justify-between w-full p-2.5 gap-2.5 rounded-f0 bg-grey7',
          isList && 'hidden',
        )}
      >
        <WebBodyText14.Bold className="text-darkestBlue">
          {t('miscScreens.tradeOptions.fixedIncome.tradeSummary.orderValue', {
            context: isEstimated ? 'estimated' : undefined,
          })}
        </WebBodyText14.Bold>

        <WebBodyText14.Bold className="text-primary">
          {displayableData.orderValue}
        </WebBodyText14.Bold>
      </div>

      <div className="flex flex-col gap-2.5">
        <div className="flex justify-between">
          <TitleText className="text-darkestBlue leading-none">
            {t('miscScreens.tradeOptions.fixedIncome.tradeSummary.orderDetails')}
          </TitleText>

          {asOf && (
            <AsOfText className="text-grey55">
              {t('miscScreens.tradeOptions.fixedIncome.tradeSummary.asOfDateTime', {
                time: asOf.toLocaleTimeString(),
                timezone: 'ET',
                date: asOf.toLocaleDateString(),
              })}
            </AsOfText>
          )}
        </div>

        <div className={cn(isGrid && 'grid grid-cols-2 gap-4', isList && 'grid grid-cols-1 gap-1')}>
          <div className={cn(isGrid && 'flex flex-col', isList && 'flex flex-row justify-between')}>
            <LabelText className="text-grey41">
              {t('miscScreens.tradeOptions.fixedIncome.tradeSummary.orderType')}
            </LabelText>

            <ValueText className="text-darkestBlue">{displayableData.orderType}</ValueText>
          </div>

          <div className={cn(isGrid && 'flex flex-col', isList && 'flex flex-row justify-between')}>
            <LabelText className="text-grey41">
              {t('miscScreens.tradeOptions.fixedIncome.tradeSummary.quantity')}
            </LabelText>

            <ValueText className="text-darkestBlue">{displayableData.quantity}</ValueText>
          </div>

          <div className={cn(isGrid && 'flex flex-col', isList && 'flex flex-row justify-between')}>
            <LabelText className="text-grey41">
              {t('miscScreens.tradeOptions.fixedIncome.tradeSummary.limitYTM')}
            </LabelText>

            <ValueText className="text-darkestBlue">{displayableData.yieldMaturity}</ValueText>
          </div>

          {displayableData.yieldToWorst && (
            <div
              className={cn(isGrid && 'flex flex-col', isList && 'flex flex-row justify-between')}
            >
              <LabelText className="text-grey41">{t('common.ytw')}</LabelText>

              <ValueText className="text-darkestBlue">{displayableData.yieldToWorst}</ValueText>
            </div>
          )}

          <div
            className={cn(
              isGrid && 'flex flex-col',
              isList && 'flex flex-row justify-between',
              !isPartialFill && 'hidden',
            )}
          >
            <LabelText className="text-grey41">
              {t('miscScreens.tradeOptions.fixedIncome.tradeSummary.partialFill')}
            </LabelText>

            <ValueText className="text-darkestBlue">{displayableData.fillingAmounts}</ValueText>
          </div>

          <div className={cn(isGrid && 'flex flex-col', isList && 'flex flex-row justify-between')}>
            <LabelText className="text-grey41">
              {t('miscScreens.tradeOptions.fixedIncome.tradeSummary.limitPrice')}
            </LabelText>

            <ValueText className="text-darkestBlue">{displayableData.limitPrice}</ValueText>
          </div>

          <div className={cn(isGrid && 'flex flex-col', isList && 'flex flex-row justify-between')}>
            <LabelText className="text-grey41">
              {t('miscScreens.tradeOptions.fixedIncome.tradeSummary.timeInForce')}
            </LabelText>

            <ValueText className="text-darkestBlue">{displayableData.timeInForce}</ValueText>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-2.5">
        <div className="flex justify-between">
          <TitleText className="text-darkestBlue leading-none">
            {t('miscScreens.tradeOptions.fixedIncome.tradeSummary.bondDetails')}
          </TitleText>

          {asOf && (
            <AsOfText className="text-grey55">
              {t('miscScreens.tradeOptions.fixedIncome.tradeSummary.asOfDateTime', {
                time: asOf.toLocaleTimeString(),
                timezone: 'ET',
                date: asOf.toLocaleDateString(),
              })}
            </AsOfText>
          )}
        </div>

        <div className={cn(isGrid && 'grid grid-cols-2 gap-4', isList && 'grid grid-cols-1 gap-1')}>
          <div className={cn(isGrid && 'flex flex-col', isList && 'flex flex-row justify-between')}>
            <LabelText className="text-grey41">
              {t('miscScreens.tradeOptions.fixedIncome.tradeSummary.issuerName')}
            </LabelText>

            <ValueText className="text-darkestBlue">{displayableData.issuerName}</ValueText>
          </div>

          <div className={cn(isGrid && 'flex flex-col', isList && 'flex flex-row justify-between')}>
            <LabelText className="text-grey41">
              {t('miscScreens.tradeOptions.fixedIncome.tradeSummary.coupon')}
            </LabelText>

            <ValueText className="text-darkestBlue">{displayableData.couponRate}</ValueText>
          </div>

          <div className={cn(isGrid && 'flex flex-col', isList && 'flex flex-row justify-between')}>
            <LabelText className="text-grey41">
              {t('miscScreens.tradeOptions.fixedIncome.tradeSummary.maturityDate')}
            </LabelText>

            <ValueText className="text-darkestBlue">{displayableData.maturityDate}</ValueText>
          </div>

          <div className={cn(isGrid && 'flex flex-col', isList && 'flex flex-row justify-between')}>
            <LabelText className="text-grey41">
              {t('miscScreens.tradeOptions.fixedIncome.tradeSummary.isin')}
            </LabelText>

            <ValueText className="text-darkestBlue">{displayableData.isin}</ValueText>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-2.5">
        <div className="flex justify-between">
          <TitleText className="text-darkestBlue leading-none">
            {isEstimated
              ? t('miscScreens.tradeOptions.fixedIncome.tradeSummary.estimatedBreakdown')
              : t('miscScreens.tradeOptions.fixedIncome.tradeSummary.breakdown')}
          </TitleText>

          {asOf && (
            <AsOfText className="text-grey55">
              {t('miscScreens.tradeOptions.fixedIncome.tradeSummary.asOfDateTime', {
                time: asOf.toLocaleTimeString(),
                timezone: 'ET',
                date: asOf.toLocaleDateString(),
              })}
            </AsOfText>
          )}
        </div>

        <div className={cn(isGrid && 'grid grid-cols-2 gap-4', isList && 'grid grid-cols-1 gap-1')}>
          <div className={cn(isGrid && 'flex flex-col', isList && 'flex flex-row justify-between')}>
            <LabelText className="text-grey41">
              {t('miscScreens.tradeOptions.fixedIncome.tradeSummary.faceValuePerBond')}
            </LabelText>

            <ValueText className="text-darkestBlue">{displayableData.faceValuePerBond}</ValueText>
          </div>

          <div className={cn(isGrid && 'flex flex-col', isList && 'flex flex-row justify-between')}>
            <LabelText className="text-grey41">
              {t('miscScreens.tradeOptions.fixedIncome.tradeSummary.accruedInterestPerBond')}
            </LabelText>

            <ValueText className="text-darkestBlue">
              {displayableData.accruedInterestPerBond}
            </ValueText>
          </div>

          <div className={cn(isGrid && 'flex flex-col', isList && 'flex flex-row justify-between')}>
            <LabelText className="text-grey41">
              {t('miscScreens.tradeOptions.fixedIncome.tradeSummary.markup')}
            </LabelText>

            <ValueText className="text-darkestBlue">{displayableData.markup}</ValueText>
          </div>

          <div className={cn(isGrid && 'flex flex-col', isList && 'flex flex-row justify-between')}>
            <LabelText className="text-grey41">
              {t('miscScreens.tradeOptions.fixedIncome.tradeSummary.commission')}
            </LabelText>

            <ValueText className="text-darkestBlue">{displayableData.commission}</ValueText>
          </div>

          <div className={cn(isGrid && 'flex flex-col', isList && 'flex flex-row justify-between')}>
            <LabelText className="text-grey41">
              {t('miscScreens.tradeOptions.fixedIncome.tradeSummary.orderValue', {
                context: isEstimated ? 'estimated' : undefined,
              })}
            </LabelText>

            <ValueText className="text-darkestBlue">{displayableData.orderValue}</ValueText>
          </div>

          <div className={cn(isGrid && 'flex flex-col', isList && 'flex flex-row justify-between')}>
            <LabelText className="text-grey41">
              {t('miscScreens.tradeOptions.fixedIncome.tradeSummary.netPrice')}
            </LabelText>

            <ValueText className="text-darkestBlue">{displayableData.netPrice}</ValueText>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FixedIncomeTradeSummary;
